(() => {
  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => [...scope.querySelectorAll(selector)];

  const liveRegion = qs('#tt-live-region');
  const announce = (message) => {
    if (!liveRegion) return;
    liveRegion.textContent = '';
    window.setTimeout(() => { liveRegion.textContent = message; }, 50);
  };

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.lastY = window.scrollY;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }
    disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
    onScroll() {
      const y = window.scrollY;
      this.classList.toggle('is-scrolled', y > 24);
      this.classList.toggle('is-hidden', y > this.lastY && y > 240);
      this.lastY = y;
    }
  }
  if (!customElements.get('sticky-header')) customElements.define('sticky-header', StickyHeader);

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = qs('form[action*="/cart/add"]', this);
      this.variantInput = qs('[name="id"]', this.form);
      this.data = JSON.parse(qs('[data-product-variants]', this)?.textContent || '[]');
      this.options = qsa('[data-option-select]', this);
      this.options.forEach((select) => select.addEventListener('change', () => this.onVariantChange()));
      qsa('[data-qty-button]', this).forEach((button) => button.addEventListener('click', () => this.changeQuantity(button)));
    }
    changeQuantity(button) {
      const input = qs('[name="quantity"]', this);
      if (!input) return;
      const next = Math.max(Number(input.min || 1), Number(input.value || 1) + Number(button.dataset.qtyButton));
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    onVariantChange() {
      const values = this.options.map((select) => select.value);
      const variant = this.data.find((item) => item.options.every((option, index) => option === values[index]));
      const button = qs('[type="submit"]', this.form);
      const price = qs('[data-product-price]', this);
      const compare = qs('[data-compare-price]', this);
      if (!variant) {
        if (button) { button.disabled = true; button.textContent = this.dataset.unavailableLabel || button.dataset.soldLabel || ''; }
        return;
      }
      this.variantInput.value = variant.id;
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      if (price) price.textContent = this.formatMoney(variant.price);
      if (compare) {
        compare.textContent = variant.compare_at_price ? this.formatMoney(variant.compare_at_price) : '';
        compare.hidden = !variant.compare_at_price || variant.compare_at_price <= variant.price;
      }
      if (button) {
        button.disabled = !variant.available;
        button.textContent = variant.available ? button.dataset.addLabel : button.dataset.soldLabel;
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      if (!window.Shopify?.designMode) window.history.replaceState({}, '', url);
    }
    formatMoney(cents) {
      const currency = document.documentElement.dataset.currency || 'USD';
      try { return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100); }
      catch (_) { return `$${(cents / 100).toFixed(2)}`; }
    }
  }
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const observer = new IntersectionObserver((entries, obs) => {
        if (!entries[0].isIntersecting) return;
        obs.unobserve(this);
        fetch(this.dataset.url)
          .then((response) => response.text())
          .then((text) => {
            const html = new DOMParser().parseFromString(text, 'text/html');
            const recommendations = html.querySelector('product-recommendations');
            if (recommendations?.innerHTML.trim()) this.innerHTML = recommendations.innerHTML;
          })
          .catch(() => {});
      }, { rootMargin: '0px 0px 400px 0px' });
      observer.observe(this);
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

  qsa('[data-announcement]').forEach((bar) => {
    const close = qs('[data-announcement-close]', bar);
    close?.addEventListener('click', () => {
      bar.hidden = true;
    });
  });

  qsa('.mobile-nav').forEach((menu) => {
    const shell = menu.closest('.header-shell');
    menu.addEventListener('toggle', () => {
      shell?.classList.toggle('is-menu-open', menu.open);
      document.body.classList.toggle('is-menu-open', menu.open);
      if (menu.open) shell?.classList.remove('is-hidden');
    });
    menu.querySelectorAll('.mobile-nav__menu a').forEach((link) => link.addEventListener('click', () => menu.removeAttribute('open')));
  });

  qsa('[data-disclosure]').forEach((details) => {
    details.addEventListener('toggle', () => {
      if (!details.open) return;
      qsa('[data-disclosure]').forEach((other) => { if (other !== details && other.closest('.header')) other.removeAttribute('open'); });
    });
  });

  if (!window.Shopify?.designMode) qsa('a[href^="#"]').forEach((link) => {
    link.addEventListener('click', (event) => {
      const id = link.getAttribute('href');
      if (id.length < 2) return;
      const target = qs(id);
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });

  const revealObserver = 'IntersectionObserver' in window && !window.Shopify?.designMode
    ? new IntersectionObserver((entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        });
      }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' })
    : null;

  const initializeReveal = (scope = document) => {
    const elements = qsa('.reveal', scope);
    elements.forEach((element) => {
      if (window.Shopify?.designMode || !revealObserver) element.classList.add('is-visible');
      else revealObserver.observe(element);
    });
  };

  initializeReveal();

  // Collection descriptions can contain legacy split/grid markup from an older theme.
  // If one half of a two-column row is genuinely empty (for example, an unset image picker),
  // remove the empty half and allow the populated half to span the full row.
  const collectionNodeHasContent = (node) => {
    if (!node || node.hidden) return false;

    const text = (node.textContent || '').replace(/\u00a0/g, ' ').trim();
    if (text) return true;

    const meaningfulMedia = qsa('img[src], video[src], video source[src], iframe[src], canvas, model-viewer, svg:not(.placeholder-svg), input, textarea, select, button', node)
      .some((el) => {
        if (el.matches('img') && !el.getAttribute('src')?.trim()) return false;
        return true;
      });
    if (meaningfulMedia) return true;

    const nodesToCheck = [node, ...qsa('[style]', node)];
    return nodesToCheck.some((el) => {
      const inlineBackground = el.style?.backgroundImage;
      if (inlineBackground && inlineBackground !== 'none') return true;
      try {
        const background = window.getComputedStyle(el).backgroundImage;
        return background && background !== 'none';
      } catch (_) {
        return false;
      }
    });
  };

  const initializeCollectionSplitRepair = (scope = document) => {
    const roots = qsa('[data-collapse-empty-columns]', scope);
    if (scope.matches?.('[data-collapse-empty-columns]')) roots.unshift(scope);

    roots.forEach((root) => {
      const candidates = [root, ...qsa('*', root)].reverse();
      candidates.forEach((container) => {
        const children = [...container.children];
        if (children.length !== 2) return;

        let display;
        try { display = window.getComputedStyle(container).display; } catch (_) { return; }
        if (display !== 'grid' && display !== 'flex' && display !== 'inline-flex') return;

        const populated = children.filter(collectionNodeHasContent);
        if (populated.length !== 1) return;

        children.forEach((child) => {
          const empty = child !== populated[0];
          child.classList.toggle('ttm-empty-column', empty);
          if (empty) child.setAttribute('aria-hidden', 'true');
        });
        container.classList.add('ttm-single-column');
      });
    });
  };

  const collectionChildHasMedia = (node) => !!node?.querySelector?.('img[src], picture img[src], video, iframe');
  const collectionChildHasMajorHeading = (node) => !!node && (node.matches?.('h1,h2,h3') || node.querySelector?.('h1,h2,h3'));

  // Turn the first image-led portion of a collection description into a deliberate
  // editorial lead: media on one side, the opening heading/body copy on the other.
  // This runs only inside the collection-description hook used above.
  const initializeCollectionLead = (scope = document) => {
    const roots = qsa('[data-collapse-empty-columns]', scope);
    if (scope.matches?.('[data-collapse-empty-columns]')) roots.unshift(scope);

    roots.forEach((root) => {
      if (root.querySelector(':scope > .ttm-collection-lead')) return;

      const children = [...root.children].filter((child) => !child.classList.contains('ttm-empty-column') && collectionNodeHasContent(child));
      if (children.length < 2) return;

      const media = children[0];
      if (!collectionChildHasMedia(media)) return;

      const copyNodes = [];
      let headingSeen = false;
      for (let index = 1; index < children.length; index += 1) {
        const child = children[index];
        if (collectionChildHasMedia(child)) break;

        const hasHeading = collectionChildHasMajorHeading(child);
        if (headingSeen && hasHeading) break;

        // A later intact two-sided grid/flex row is a new section, not intro copy.
        if (copyNodes.length && child.children.length === 2) {
          let display = '';
          try { display = window.getComputedStyle(child).display; } catch (_) {}
          const filled = [...child.children].filter(collectionNodeHasContent);
          if ((display === 'grid' || display === 'flex' || display === 'inline-flex') && filled.length === 2) break;
        }

        copyNodes.push(child);
        if (hasHeading) headingSeen = true;
      }

      if (!copyNodes.length || !headingSeen) return;

      const lead = document.createElement('div');
      lead.className = 'ttm-collection-lead';
      const copy = document.createElement('div');
      copy.className = 'ttm-collection-lead__copy';

      media.before(lead);
      media.classList.add('ttm-collection-lead__media');
      lead.append(media, copy);
      copyNodes.forEach((node) => copy.append(node));
    });
  };

  initializeCollectionSplitRepair();
  initializeCollectionLead();
  document.addEventListener('shopify:section:load', (event) => {
    initializeReveal(event.target);
    initializeCollectionSplitRepair(event.target);
    initializeCollectionLead(event.target);
  });
  document.addEventListener('shopify:block:select', (event) => {
    event.target.closest('.reveal')?.classList.add('is-visible');
    initializeReveal(event.target);
  });

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class ReviewCarousel extends HTMLElement {
    connectedCallback() {
      this.slides = qsa('[data-carousel-slide]', this);
      if (!this.slides.length) return;
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this.prevBtn = qs('[data-carousel-prev]', this);
      this.nextBtn = qs('[data-carousel-next]', this);
      this.buildDots();
      this.prevBtn?.addEventListener('click', () => this.manual(this.index - 1));
      this.nextBtn?.addEventListener('click', () => this.manual(this.index + 1));
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.restart());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.restart());
      this.tabIndex = 0;
      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.manual(this.index - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.manual(this.index + 1); }
      });
      this.bindSwipe();
      this.go(this.index);
      this.restart();
    }
    disconnectedCallback() { this.stop(); }
    canAuto() { return this.dataset.autoplay === 'true' && !prefersReducedMotion && this.slides.length > 1; }
    buildDots() {
      this.dotsWrap = qs('[data-carousel-dots]', this);
      if (!this.dotsWrap) return;
      this.dots = this.slides.map((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'ttm-quote-carousel__dot';
        dot.setAttribute('aria-label', `Show review ${i + 1}`);
        dot.addEventListener('click', () => this.manual(i));
        this.dotsWrap.appendChild(dot);
        return dot;
      });
    }
    go(target) {
      const n = this.slides.length;
      this.index = ((target % n) + n) % n;
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      this.dots?.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this.index);
        dot.setAttribute('aria-current', i === this.index ? 'true' : 'false');
      });
    }
    manual(target) { this.go(target); this.restart(); }
    start() {
      this.stop();
      const interval = Number(this.dataset.interval || 6000);
      this.timer = window.setInterval(() => this.go(this.index + 1), interval);
    }
    stop() { if (this.timer) { window.clearInterval(this.timer); this.timer = null; } }
    restart() { if (this.canAuto()) this.start(); }
    bindSwipe() {
      let x0 = null;
      this.addEventListener('pointerdown', (e) => { x0 = e.clientX; });
      this.addEventListener('pointerup', (e) => {
        if (x0 === null) return;
        const dx = e.clientX - x0;
        if (Math.abs(dx) > 45) this.manual(this.index + (dx < 0 ? 1 : -1));
        x0 = null;
      });
    }
  }
  if (!customElements.get('review-carousel')) customElements.define('review-carousel', ReviewCarousel);

  const parallax = qsa('[data-parallax]');
  if (parallax.length && !prefersReducedMotion && 'IntersectionObserver' in window) {
    let ticking = false;
    const update = () => {
      const vh = window.innerHeight || 1;
      parallax.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > vh + 200) return;
        const progress = (rect.top + rect.height / 2 - vh / 2) / vh;
        el.style.setProperty('--par', (progress * -20).toFixed(1) + 'px');
      });
      ticking = false;
    };
    const onScroll = () => { if (!ticking) { ticking = true; window.requestAnimationFrame(update); } };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  const waveReviewSections = qsa('[data-wave-reviews]');
  if (waveReviewSections.length && !prefersReducedMotion) {
    let waveTicking = false;
    const updateWaveReviewTitles = () => {
      const vh = window.innerHeight || 1;
      waveReviewSections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > vh) return;
        const travel = Math.max(1, rect.height + vh);
        const progress = Math.min(1, Math.max(0, (vh - rect.top) / travel));
        const shift = (progress - 0.5) * 240;
        section.style.setProperty('--wave-title-shift-a', `${shift.toFixed(1)}px`);
        section.style.setProperty('--wave-title-shift-b', `${(shift * 0.7).toFixed(1)}px`);
      });
      waveTicking = false;
    };
    const onWaveScroll = () => {
      if (waveTicking) return;
      waveTicking = true;
      window.requestAnimationFrame(updateWaveReviewTitles);
    };
    window.addEventListener('scroll', onWaveScroll, { passive: true });
    window.addEventListener('resize', onWaveScroll, { passive: true });
    updateWaveReviewTitles();
  }

  qsa('[data-copy-code]').forEach((button) => button.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(button.dataset.copyCode);
      announce('Discount code copied');
      button.textContent = 'Copied';
    } catch (_) {}
  }));
})();
